import { Component, ojs, app, v, each, state } from "modular-openscriptjs";
import { tool, dom } from "../../fotastart";
import {
    monetary,
    empty,
    cleanDispose,
    inform,
    insertSpaces,
    randomColor,
    either,
    updateStateValue,
    uniqueId,
} from "../../helpers";
import CheckYesno from "../Blocks/CheckYesno";
import { STRINGS } from "../../declarations";
import { $e } from "../../ojs.events";

const h = app("h");
const dc = app("dc");
const gc = app("gc");
const broker = app("broker");

ojs(CheckYesno);

export default class OrderItem extends Component {
    imagesModal = null;
    expanded = state(false);

    render(item, status, ...args) {
        if (!item) return h.empty();

        const name = item?.name || item?.product?.name || "Item";
        const quantity = item?.quantity ?? 1;
        const currency = item?.currency || gc.user?.currency || "KES";
        const estimatedPrice = item?.price ?? 0;
        const purchasedPrice = item?.purchase_price;

        const hasFeatures = item?.features && item?.features.length > 0;
        const hasExtras = item?.extras && item?.extras.length > 0;

        // A business-funded deal (deal.company_id set) changes what the
        // deliverer actually pays the business, so we show the discounted
        // price + deal name here to justify it. A global/Carata-funded deal
        // (deal.company_id === null) does not change what the business
        // charges the deliverer, so it gets no discount UI.
        const deal = item?.deal;
        const hasBusinessDeal = !!(
            deal &&
            deal.company_id != null &&
            item?.discounted_price != null
        );

        const image =
            item?.product?.thumbnail ||
            item?.product?.images?.[0]?.path ||
            (item?.images && item?.images[0]?.path);

        return h.div(
            {
                class: `p-3 border rounded-4 mb-3 shadow-sm ${item.purchased == 1 ? "bg-secondary-subtle" : "bg-white"}`,
                style: `border-color: #${randomColor()}5c; max-width: 540px;`,
            },
            h.div(
                { class: "d-flex flex-column gap-3" },

                // Image + Name + Prices Info Block
                h.div(
                    { class: "d-flex gap-3 align-items-start" },
                    either(
                        image,
                        () =>
                            h.img({
                                src: image,
                                class: "rounded-3 object-fit-cover border flex-shrink-0",
                                style: "width: 70px; height: 70px;",
                                onclick: this.method("seeImages", [
                                    item?.images || [{ path: image }],
                                ]),
                            }),
                        () =>
                            h.div(
                                {
                                    class: "bg-light rounded-3 d-flex align-items-center justify-content-center text-secondary border flex-shrink-0",
                                    style: "width: 70px; height: 70px;",
                                },
                                h.i({ class: "bi bi-box fs-3" }),
                            ),
                    ),

                    h.div(
                        { class: "flex-fill min-w-0" },
                        h.h5(
                            { class: "fw-bold text-dark mb-1 text-wrap" },
                            name,
                        ),
                        h.div(
                            {
                                class: "d-flex flex-column gap-1 fs-sm text-muted",
                            },
                            either(
                                hasBusinessDeal,
                                // item.price is already deal-aware (it's
                                // reverse-bracketed from the discounted total
                                // for a business-funded deal - see
                                // OrderService::computeItemFinancials()), so
                                // estimatedPrice here already IS the reduced
                                // amount to pay. item.discounted_price is a
                                // different, larger bracket (full retail
                                // price including Carata's markup) and isn't
                                // a further discount on top of this - it's
                                // only shown separately below in the "Set
                                // Purchase Details" flow.
                                () =>
                                    h.span(
                                        {
                                            class: "d-flex align-items-center flex-wrap gap-2",
                                        },
                                        "Est. Price: ",
                                        h.strong(
                                            { class: "text-success" },
                                            monetary(estimatedPrice, currency),
                                        ),
                                        h.span(
                                            {
                                                class: "badge bg-success-subtle text-success border border-success-subtle d-inline-flex align-items-center gap-1",
                                            },
                                            h.i({ class: "bi bi-tag-fill" }),
                                            `Deal: ${deal.name} applied`,
                                        ),
                                    ),
                                () =>
                                    h.span(
                                        "Est. Price: ",
                                        h.strong(
                                            { class: "text-dark" },
                                            monetary(estimatedPrice, currency),
                                        ),
                                    ),
                            ),
                            either(item.purchased == 1, () =>
                                h.span(
                                    "Purchased At: ",
                                    h.strong(
                                        { class: "text-success" },
                                        monetary(purchasedPrice, currency),
                                    ),
                                ),
                            ),
                            either(item?.company_id, () =>
                                h.span(
                                    { class: "fs-xsm" },
                                    "Business Commission: ",
                                    h.strong(
                                        { class: "text-dark" },
                                        `${Math.ceil(item?.business_commission_rate ?? 0)}%`,
                                    ),
                                    either(item?.business_commission > 0, () =>
                                        h.span(
                                            " (",
                                            monetary(
                                                item.business_commission,
                                                currency,
                                            ),
                                            ")",
                                        ),
                                    ),
                                ),
                            ),
                        ),
                    ),
                ),

                // Quantity and Description Callout
                h.div(
                    {
                        class: "d-flex flex-column gap-2 bg-light p-3 rounded-3 border border-light-subtle",
                    },
                    h.div(
                        {
                            class: "text-primary fw-bold fs-6 d-flex align-items-center gap-2",
                        },
                        h.i({ class: "bi bi-cart-fill" }),
                        `Quantity: ${quantity}`,
                    ),
                    this.renderDescription(item?.description),
                ),

                // Item Status Text (only when company_id is present)
                either(item?.company_id, () => {
                    let statusText = "";
                    let icon = "bi-hourglass-split";
                    let textClass = "text-secondary";

                    switch (item?.status) {
                        case "queued":
                            statusText =
                                "The seller has not started preparing this item. Call to follow up";
                            icon = "bi-list-ul";
                            textClass = "text-secondary";
                            break;
                        case "preparing":
                            statusText =
                                "The seller is preparing this item. Call to follow up";
                            icon = "bi-hammer";
                            textClass = "text-primary";
                            break;
                        case "ready":
                            statusText =
                                "The seller has prepared this item and is waiting for you.";
                            icon = "bi-check2-circle";
                            textClass = "text-success";
                            break;
                        case "picked":
                            statusText =
                                "This item has been purchased and picked up.";
                            icon = "bi-box-seam-fill";
                            textClass = "text-muted";
                            break;
                        default:
                            statusText = `Status: ${item?.status || "queued"}`;
                            break;
                    }

                    return h.div(
                        {
                            class: `d-flex align-items-start gap-2 fs-xsm ${textClass} bg-light p-2 rounded-3 border border-light-subtle`,
                        },
                        h.i({ class: `bi ${icon} fs-6` }),
                        h.span({ class: "fw-semibold" }, statusText),
                    );
                }),

                // Features
                either(hasFeatures, () =>
                    h.div(
                        { class: "d-flex flex-wrap gap-2 w-100 my-1" },
                        item.features.map((f) =>
                            h.span(
                                {
                                    class: "badge bg-warning-subtle text-dark border border-warning rounded-3 py-2 px-3",
                                },
                                `${f.feature_name}: `,
                                h.strong(
                                    { class: "text-dark-emphasis fw-bold" },
                                    f.feature_value,
                                ),
                            ),
                        ),
                    ),
                ),

                // Buy From Block (positioned right before purchase button)
                v(dc.buyFromSomewhereElse, (buyFromSomewhereElse) => {
                    const isSomewhereElse =
                        buyFromSomewhereElse.value[item.id] === true;
                    if (isSomewhereElse) return "";

                    const purchaseLocation = item?.purchase_location;
                    if (
                        purchaseLocation &&
                        (purchaseLocation.description ||
                            purchaseLocation.contact_phone)
                    ) {
                        return h.div(
                            { class: "bg-light border rounded-3 p-3 w-100" },
                            h.div(
                                {
                                    class: "small text-muted text-uppercase fw-bold mb-2 d-flex align-items-center gap-1",
                                },
                                h.i({
                                    class: "bi bi-geo-alt-fill text-danger fs-5",
                                }),
                                "Buy From:",
                            ),
                            h.div(
                                {
                                    class: "d-flex justify-content-between align-items-center",
                                },
                                h.div(
                                    { class: "flex-fill min-w-0" },
                                    h.span(
                                        {
                                            class: "fw-bold text-dark d-block fs-sm",
                                        },
                                        purchaseLocation.description ||
                                            "Specified Location",
                                    ),
                                    either(
                                        purchaseLocation.latitude &&
                                            purchaseLocation.longitude,
                                        () =>
                                            h.a(
                                                {
                                                    href: `https://www.google.com/maps/search/?api=1&query=${purchaseLocation.latitude},${purchaseLocation.longitude}`,
                                                    target: "_blank",
                                                    class: "text-primary text-decoration-none fs-xsm d-flex align-items-center gap-1 mt-1",
                                                },
                                                h.i({ class: "bi bi-map" }),
                                                "View on map",
                                            ),
                                    ),
                                ),
                                either(purchaseLocation.contact_phone, () =>
                                    h.a(
                                        {
                                            href: `tel:${purchaseLocation.contact_phone}`,
                                            class: "btn btn-outline-success btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0 flex-shrink-0 ms-2",
                                            style: "width: 32px; height: 32px;",
                                        },
                                        h.i({ class: "bi bi-telephone-fill" }),
                                    ),
                                ),
                            ),
                        );
                    }

                    return h.div(
                        {
                            class: "bg-light border rounded-3 p-3 border-dashed w-100",
                        },
                        h.div(
                            {
                                class: "small text-muted text-uppercase fw-bold mb-2 d-flex align-items-center gap-1",
                            },
                            h.i({ class: "bi bi-geo-alt text-muted fs-5" }),
                            "Buy From:",
                        ),
                        h.span(
                            { class: "text-muted fs-xsm fst-italic" },
                            "Follow the purchase directions to find where to buy from.",
                        ),
                    );
                }),

                // Purchase / Actions Block
                h.div(
                    {
                        class: "d-flex flex-column align-items-center gap-2 mt-1 w-100",
                    },
                    either(
                        item.purchased == 1,
                        () =>
                            h.div(
                                {
                                    class: "d-flex align-items-center justify-content-center gap-2 text-success fw-bold py-2 w-100",
                                    style: "cursor: pointer;",
                                    onclick: this.method(
                                        "viewPurchase",
                                        item.id,
                                    ),
                                    id: `order-item-${item.id}-purchases`,
                                },
                                h.i({
                                    class: "bi bi-check-circle-fill text-success fs-5",
                                }),
                                h.span("View Purchases"),
                            ),
                        () =>
                            either(status === "processing", () =>
                                v(
                                    dc.selectedItems,
                                    (selectedItems) => {
                                        const isSelected =
                                            selectedItems.value.hasOwnProperty(
                                                item.id,
                                            );
                                        const price =
                                            dc.purchasePrices.value[item.id] !==
                                            undefined
                                                ? dc.purchasePrices.value[
                                                      item.id
                                                  ]
                                                : estimatedPrice;

                                        // The deliverer pays for packaging too - fold it
                                        // into the per-unit price before multiplying by
                                        // quantity, same as the purchase confirmation total.
                                        const packagePrice =
                                            dc.packagePrices.value[item.id] ??
                                            item?.package_price ??
                                            item?.product?.package_price ??
                                            0;
                                        const totalUnitPrice =
                                            Number(price) + Number(packagePrice);
                                        const totalPrice =
                                            quantity * totalUnitPrice;

                                        if (isSelected) {
                                            return h.div(
                                                {
                                                    class: "d-flex flex-column align-items-center gap-2 w-100",
                                                    id: `order-item-${item.id}-purchase-btn`,
                                                },
                                                h.div(
                                                    {
                                                        class: "bg-success text-white rounded-3 shadow-sm w-100 p-3 fw-bold d-flex align-items-center justify-content-between",
                                                    },
                                                    h.div(
                                                        {
                                                            class: "d-flex flex-column gap-2",
                                                        },
                                                        h.span(
                                                            `Paying ${currency} ${totalPrice}`,
                                                        ),
                                                        either(
                                                            quantity > 1,
                                                            () =>
                                                                h.span(
                                                                    {
                                                                        class: "badge rounded-pill bg-success-subtle text-dark",
                                                                    },
                                                                    ` ${quantity} at ${currency} ${totalUnitPrice} each`,
                                                                ),
                                                        ),
                                                        either(
                                                            hasBusinessDeal,
                                                            () =>
                                                                h.span(
                                                                    {
                                                                        class: "badge bg-white text-success border border-success d-inline-flex align-items-center gap-1 align-self-start",
                                                                    },
                                                                    h.i({
                                                                        class: "bi bi-tag-fill",
                                                                    }),
                                                                    `Deal: ${deal?.name} applied`,
                                                                ),
                                                        ),
                                                    ),

                                                    h.span(
                                                        {
                                                            class: "fs-5 fw-bold",
                                                            style: "cursor: pointer; padding-left: 10px; line-height: 1;",
                                                            onclick:
                                                                this.method(
                                                                    "deselectItem",
                                                                    [
                                                                        "${this}",
                                                                        item,
                                                                    ],
                                                                ),
                                                        },
                                                        "×",
                                                    ),
                                                ),
                                                h.a(
                                                    {
                                                        class: "text-primary text-decoration-underline",
                                                        style: "cursor: pointer; font-size: 0.75rem;",
                                                        onclick: this.method(
                                                            "openPurchaseModal",
                                                            ["${this}", item],
                                                        ),
                                                        id: `order-item-${item.id}-update-info`,
                                                    },
                                                    "update purchase information",
                                                ),
                                            );
                                        } else {
                                            return h.div(
                                                {
                                                    class: "d-flex flex-column align-items-center gap-2 w-100",
                                                    id: `order-item-${item.id}-purchase-btn-holder`,
                                                },
                                                h.button(
                                                    {
                                                        id: `order-item-${item.id}-purchase-btn`,
                                                        class: "btn btn-outline-success rounded-pill shadow-sm w-100 py-2 fw-bold d-flex align-items-center justify-content-center gap-2",
                                                        onclick: this.method(
                                                            "openPurchaseModal",
                                                            ["${this}", item],
                                                        ),
                                                    },
                                                    h.i({
                                                        class: "bi bi-shop fs-5",
                                                    }),
                                                    h.span("Purchase"),
                                                ),
                                            );
                                        }
                                    },
                                    {
                                        $class: "d-block w-100",
                                        class: "d-block w-100",
                                    },
                                ),
                            ),
                    ),
                ),
            ),
            ...args,
        );
    }

    deselectItem(elem, item) {
        updateStateValue(dc.orderItemsMapping, (current) => {
            if (current && current[item.order_id]) {
                delete current[item.order_id][item.id];
            }
            return current;
        });

        updateStateValue(dc.selectedItems, (current) => {
            if (current) {
                delete current[item.id];
            }
            return current;
        });

        broker.send($e.app.has.selectedOrderItems);
    }

    openPurchaseModal(btn, item) {
        const modalId = uniqueId();

        const isSelected = dc.selectedItems.value.hasOwnProperty(item.id);
        const name = item?.name || "this item";
        const estimatedPrice = item?.price ?? 0;
        const currency = gc.user?.currency || "KES";
        const currentPrice = dc.purchasePrices.value[item.id]
            ? dc.purchasePrices.value[item.id]
            : estimatedPrice;
        const currentPackagePrice = dc.packagePrices.value[item.id]
            ? dc.packagePrices.value[item.id]
            : (item?.package_price ?? item?.product?.package_price ?? 0);
        const isSomewhereElse = dc.buyFromSomewhereElse.value[item.id] === true;
        const buyAnother = dc.buyAnotherItem.value[item.id] === true;
        const isBuyingThisItem = !buyAnother;

        const businessName =
            item?.purchase_location?.description ||
            item?.owner?.display_name ||
            "Designated Business";

        const deal = item?.deal;
        const hasBusinessDeal = !!(
            deal &&
            deal.company_id != null &&
            item?.discounted_price != null
        );

        let purchaseModal = null;

        purchaseModal = tool.plainModal(
            h.div(
                { class: "modal-content border-0 rounded-4 p-4", id: `modal-${modalId}` },
                h.div(
                    {
                        class: "modal-header border-0 pb-0 justify-content-between",
                    },
                    h.h5(
                        { class: "modal-title fw-bold text-black fs-5" },
                        "Set Purchase Details",
                    ),
                    h.button({
                        type: "button",
                        class: "btn-close",
                        data_bs_dismiss: "modal",
                        aria_label: "Close",
                    }),
                ),
                h.div(
                    { class: "modal-body pt-3" },
                    h.div(
                        {
                            class: "p-3 bg-light rounded-3 mb-3 border border-light-subtle",
                        },
                        h.div(
                            {
                                class: "mb-3 pb-3 border-bottom border-secondary-subtle",
                            },
                            h.CheckYesno(
                                {
                                    id: `modal-buy-this-item-${item.id}`,
                                    checked: isBuyingThisItem,
                                    label: {
                                        value: "Is this the item you're buying?",
                                        bold: true,
                                    },
                                },
                                {
                                    class: "check-yesno-end d-flex justify-content-between align-items-center w-100",
                                },
                            ),
                        ),
                        h.span(
                            {
                                class: "text-muted fs-xsm text-uppercase fw-bold d-block mb-1",
                            },
                            "Item:",
                        ),
                        h.span({ class: "fw-bold text-dark fs-6" }, name),
                    ),
                    either(hasBusinessDeal, () =>
                        h.div(
                            {
                                class: "alert alert-success py-2 px-3 mb-3 d-flex align-items-start gap-2 fs-xsm",
                            },
                            h.i({ class: "bi bi-tag-fill fs-6 mt-1" }),
                            h.span(
                                h.strong({}, `Business Deal: ${deal.name}. `),
                                // estimatedPrice (item.price) is already the
                                // deal-discounted amount to pay - it's
                                // reverse-bracketed from the discounted total
                                // for a business deal (see
                                // OrderService::computeItemFinancials()).
                                // item.discounted_price is a different,
                                // larger bracket (full retail price
                                // including Carata's markup) and is not the
                                // amount to pay here.
                                `Discounted price: ${currency} ${estimatedPrice}. Show this deal to the business as justification for paying less.`,
                            ),
                        ),
                    ),
                    h.div(
                        { class: "mb-3" },
                        h.label(
                            {
                                class: "form-label text-dark-emphasis fw-bold fs-6 mb-2",
                                for: `modal-purchase-price-${item.id}`,
                            },
                            "What is the price of one of this item?",
                        ),
                        h.div(
                            {
                                class: "input-group input-group-lg shadow-sm mb-3",
                            },
                            h.span(
                                {
                                    class: "input-group-text bg-light text-secondary border-secondary-subtle",
                                },
                                `${currency}:`,
                            ),
                            h.input({
                                class: "form-control border-secondary-subtle fw-semibold text-dark",
                                id: `modal-purchase-price-${item.id}`,
                                placeholder: estimatedPrice.toString(),
                                type: "number",
                                value: currentPrice,
                                style: "font-size: 1.25rem;",
                            }),
                        ),
                    ),

                    // Toggle boxes / Radio buttons
                    h.div(
                        { class: "d-flex flex-column gap-2 mb-3" },
                        h.span(
                            { class: "fw-bold text-muted fs-xsm d-block mb-1" },
                            "Where are you buying this item from?",
                        ),

                        // Option 1: Buy at Business
                        h.div(
                            {
                                class: "form-check p-3 border rounded-3 d-flex align-items-center gap-2",
                                style: "cursor: pointer;",
                            },
                            h.input(
                                {
                                    class: "form-check-input ms-0",
                                    type: "radio",
                                    name: `purchase_source_${item.id}`,
                                    id: `modal-buy-at-business-${item.id}`,
                                },
                                either(!isSomewhereElse, { checked: true }),
                            ),
                            h.label(
                                {
                                    class: "form-check-label fw-bold text-dark-emphasis mb-0 ps-2",
                                    for: `modal-buy-at-business-${item.id}`,
                                    style: "cursor: pointer;",
                                },
                                `${businessName}`,
                            ),
                        ),

                        // Option 2: Buy somewhere else
                        h.div(
                            {
                                class: "form-check p-3 border rounded-3 d-flex align-items-center gap-2",
                                style: "cursor: pointer;",
                            },
                            h.input(
                                {
                                    class: "form-check-input ms-0",
                                    type: "radio",
                                    name: `purchase_source_${item.id}`,
                                    id: `modal-somewhere-else-${item.id}`,
                                },
                                either(isSomewhereElse, { checked: true }),
                            ),
                            h.label(
                                {
                                    class: "form-check-label fw-bold text-dark-emphasis mb-0 ps-2",
                                    for: `modal-somewhere-else-${item.id}`,
                                    style: "cursor: pointer;",
                                },
                                "Somewhere else",
                            ),
                        ),
                    ),

                    // Packaging cost - only meaningful for products that need a
                    // container (e.g. rice/beef/stew meals). Prefilled from the
                    // product's own package_price; the rider can correct it here to
                    // what it actually cost, same as the price field above.
                    h.div(
                        { class: "mb-3" },
                        h.label(
                            {
                                class: "form-label text-dark-emphasis fw-bold fs-6 mb-2",
                                for: `modal-package-price-${item.id}`,
                            },
                            "Packaging cost (if any)",
                        ),
                        h.div(
                            {
                                class: "input-group input-group-lg shadow-sm",
                            },
                            h.span(
                                {
                                    class: "input-group-text bg-light text-secondary border-secondary-subtle",
                                },
                                `${currency}:`,
                            ),
                            h.input({
                                class: "form-control border-secondary-subtle fw-semibold text-dark",
                                id: `modal-package-price-${item.id}`,
                                placeholder: "0",
                                type: "number",
                                value: currentPackagePrice,
                                style: "font-size: 1.25rem;",
                            }),
                        ),
                        h.small(
                            { class: "text-muted mt-1 d-block" },
                            "Leave as-is if you didn't pay separately for packaging.",
                        ),
                    ),

                    h.button(
                        {
                            class: `btn ${isSelected ? "bg-blue text-white" : "btn-success"} btn-lg w-100 fw-bold rounded-3 mt-2`,
                            listeners: {
                                click: () => {
                                    const modal = dom.id(`modal-${modalId}`);

                                    // Read the price straight from the field at save time
                                    // instead of relying on an intermediate "input" event
                                    // cache, which could be stale/unset and silently fall
                                    // back to the item's default price.
                                    const priceInput = dom.get(
                                        `#modal-purchase-price-${item.id}`,
                                        modal
                                    );

                                    const rawPrice = priceInput?.value;
                                    const enteredPrice =
                                        rawPrice !== undefined &&
                                        rawPrice !== ""
                                            ? parseFloat(rawPrice)
                                            : currentPrice;

                                    const packagePriceInput = dom.get(
                                        `#modal-package-price-${item.id}`,
                                        modal
                                    );

                                    const rawPackagePrice =
                                        packagePriceInput?.value;
                                    const enteredPackagePrice =
                                        rawPackagePrice !== undefined &&
                                        rawPackagePrice !== ""
                                            ? parseFloat(rawPackagePrice)
                                            : currentPackagePrice;

                                    const somewhereElseRadio =
                                        dom.get(
                                            `#modal-somewhere-else-${item.id}`,
                                            modal
                                        );
                                    const boughtSomewhereElse =
                                        somewhereElseRadio
                                            ? somewhereElseRadio.checked
                                            : false;

                                    const buyThisItemCheckbox =
                                        dom.get(
                                            `#modal-buy-this-item-${item.id}`,
                                            modal
                                        );
                                    const buyAnotherItemValue =
                                        buyThisItemCheckbox
                                            ? !buyThisItemCheckbox.checked
                                            : false;

                                    // Save values
                                    updateStateValue(
                                        dc.purchasePrices,
                                        (current) => {
                                            current ??= {};

                                            current[item.id] = isNaN(
                                                enteredPrice,
                                            )
                                                ? currentPrice
                                                : enteredPrice;

                                            return current;
                                        },
                                    );

                                    updateStateValue(
                                        dc.packagePrices,
                                        (current) => {
                                            current ??= {};

                                            current[item.id] = isNaN(
                                                enteredPackagePrice,
                                            )
                                                ? currentPackagePrice
                                                : enteredPackagePrice;

                                            return current;
                                        },
                                    );

                                    updateStateValue(
                                        dc.buyFromSomewhereElse,
                                        (current) => {
                                            current ??= {};
                                            current[item.id] =
                                                boughtSomewhereElse;
                                            return current;
                                        },
                                    );

                                    updateStateValue(
                                        dc.buyAnotherItem,
                                        (current) => {
                                            current ??= {};
                                            current[item.id] =
                                                buyAnotherItemValue;
                                            return current;
                                        },
                                    );

                                    updateStateValue(
                                        dc.orderItemsMapping,
                                        (current) => {
                                            current ??= {};
                                            current[item.order_id] ??= {};
                                            current[item.order_id][item.id] =
                                                item;

                                            return current;
                                        },
                                    );

                                    updateStateValue(
                                        dc.selectedItems,
                                        (current) => {
                                            current ??= {};
                                            current[item.id] = item;
                                            return current;
                                        },
                                    );

                                    broker.send($e.app.has.selectedOrderItems);
                                    purchaseModal?.hide();

                                    setTimeout(() => {
                                        cleanDispose(purchaseModal);
                                    }, 1000);
                                },
                            },
                        },
                        isSelected ? "Update purchase" : "Save & Select to Buy",
                    ),
                ),
            ),
            {},
        );

        purchaseModal.show();
    }

    setPurchasePrice(elem, itemId) {
        if (elem.value.length < 1 && elem.value <= 0) {
            delete dc.purchasePrices.value[itemId];
        } else {
            dc.purchasePrices.value[itemId] = elem.value;
        }
    }

    seeImages(images) {
        if (this.imagesModal) {
            cleanDispose(this.imagesModal);
        }

        this.imagesModal = tool.plainModal(
            h.div(
                { class: "modal-content border-0 w-auto mx-auto" },
                h.div(
                    { class: "modal-body p-0 px-3" },
                    h.ProductImageView(images),
                ),
            ),
            {},
        );

        this.imagesModal.show();
    }

    selectItem(elem, item) {
        let items = tool.deepCopy(dc.selectedItems.value);
        let orders = tool.deepCopy(dc.orderItemsMapping.value);

        if (!(item.order_id in orders)) {
            orders[item.order_id] = {};
        }

        if (items.hasOwnProperty(item.id)) {
            delete items[item.id];
            delete orders[item.order_id][item.id];
        } else {
            items[item.id] = item;
            orders[item.order_id][item.id] = item;
        }

        dc.orderItemsMapping.value = orders;
        dc.selectedItems.value = items;

        broker.send($e.app.has.selectedOrderItems);
    }

    cleanUp() {
        super.cleanUp();
        cleanDispose(this.imagesModal);
    }

    viewPurchase(itemId) {
        broker.send($e.purchase.needs.display, payload({ itemId }));
    }

    pluralizeExtra(qty, name) {
        if (qty <= 1) return `${qty} ${name}`;
        let pluralName = name;
        if (!name.endsWith("s")) {
            if (name.endsWith("y")) {
                pluralName = name.slice(0, -1) + "ies";
            } else if (
                name.endsWith("ch") ||
                name.endsWith("sh") ||
                name.endsWith("x")
            ) {
                pluralName = name + "es";
            } else {
                pluralName = name + "s";
            }
        }
        return `${qty} ${pluralName}`;
    }

    renderDescription(desc) {
        if (!desc) return null;

        const limit = 80;
        if (desc.length <= limit) {
            return h.div({ class: "text-muted fs-xsm" }, desc);
        }

        return v(this.expanded, (isExpanded) => {
            if (isExpanded.value) {
                return h.div(
                    { class: "text-muted fs-xsm" },
                    desc,
                    " ",
                    h.a(
                        {
                            href: "#",
                            class: "text-primary fw-bold text-decoration-none ms-1",
                            listeners: {
                                click: (e) => {
                                    e.preventDefault();
                                    this.expanded.value = false;
                                },
                            },
                        },
                        "Read less",
                    ),
                );
            } else {
                return h.div(
                    { class: "text-muted fs-xsm" },
                    desc.substring(0, limit) + "...",
                    " ",
                    h.a(
                        {
                            href: "#",
                            class: "text-primary fw-bold text-decoration-none ms-1",
                            listeners: {
                                click: (e) => {
                                    e.preventDefault();
                                    this.expanded.value = true;
                                },
                            },
                        },
                        "Read more",
                    ),
                );
            }
        });
    }
}
